import { useOnboarding } from "@/components/owner/OnboardingProvider";
import { useOwnerAuth } from "@/components/owner/OwnerAuthProvider";
import { db } from "@/lib/firebase";
import { hasOwnerCorrectionSession } from "@/lib/ownerCorrectionSession";
import {
  normalizeOwnerOnboardingStatus,
  resolveOwnerAuthGate,
  resolveOwnerOnboardingRouteDecision,
  selectOwnerLifecycleBusiness,
} from "@/lib/onboardingResume";
import { router, useLocalSearchParams, usePathname, useSegments } from "expo-router";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, Text, View } from "react-native";

type GuardBusiness = {
  id: string;
  status?: unknown;
  isDeleted?: boolean;
  isPublished?: boolean;
  updatedAt?: unknown;
  createdAt?: unknown;
};

function firstParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

function toMillis(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value instanceof Date) return value.getTime();
  if (
    value &&
    typeof value === "object" &&
    "toMillis" in value &&
    typeof (value as { toMillis?: unknown }).toMillis === "function"
  ) {
    return (value as { toMillis: () => number }).toMillis();
  }
  return 0;
}

export default function OwnerOnboardingGate({
  children,
}: {
  children: React.ReactNode;
}) {
  const { authInitialized, user, ownerReady, ownerError } = useOwnerAuth();
  const { setBusinessId, setLifecycleAccess } = useOnboarding();
  const segments = useSegments();
  const pathname = usePathname();
  const currentPathnameRef = useRef(pathname);
  currentPathnameRef.current = pathname;
  const params = useLocalSearchParams<{
    businessId?: string | string[];
    mode?: string | string[];
  }>();
  const [readyContextKey, setReadyContextKey] = useState<string | null>(null);

  const routeName = String(segments[segments.length - 1] ?? "");
  const segmentPath = segments.join("/");
  const isSignInRoute = pathname === "/onboarding/signin";
  const isPhoneBackupRoute = routeName === "phone-backup";
  const isOnboardingIndex =
    segmentPath === "(owner)/onboarding" || routeName === "index";
  // All remaining routes (including both location screens) share one guard.
  // Leaf navigation must not tear down the Stack that is handling that navigation.
  const routeCategory = isSignInRoute
    ? "signin"
    : isPhoneBackupRoute
      ? "phone-backup"
      : isOnboardingIndex
        ? "onboarding-index"
        : "guarded-onboarding-leaf";
  const ownerId = user?.uid;
  const requestedBusinessId = firstParam(params.businessId);
  const requestedMode = firstParam(params.mode) ?? "onboarding";
  const approvedEditMode = requestedMode === "edit-approved";
  const authGate = resolveOwnerAuthGate({
    authInitialized,
    hasUser: !!user,
    ownerReady,
    ownerError,
  });

  const accessContextKey = useMemo(
    () =>
      JSON.stringify([
        ownerId ?? "",
        authGate,
        routeCategory,
        requestedBusinessId ?? "",
        requestedMode,
      ]),
    [ownerId, authGate, routeCategory, requestedBusinessId, requestedMode]
  );
  // A previous decision cannot render a new access context while its effect is
  // still pending. This state starts empty on every mount, including direct URLs.
  const guardReady = readyContextKey === accessContextKey;

  useEffect(() => {
    let cancelled = false;
    const replaceIfDifferent = (destination: string) => {
      // Expo's pathname omits route groups. Keep existing query params when
      // already at the destination, and use the latest path after async reads.
      const destinationPath = destination.replace(/^\/\(owner\)(?=\/|$)/, "");
      if (cancelled || currentPathnameRef.current === destinationPath) return;
      router.replace(destination as any);
    };

    if (isSignInRoute) {
      setReadyContextKey(accessContextKey);
      return () => {
        cancelled = true;
      };
    }

    // Preserve an applicable decision on incidental rerenders. Only a genuine
    // context change invalidates it; changing leaf names never changes the key.
    setReadyContextKey((previous) =>
      previous === accessContextKey ? previous : null
    );

    if (authGate === "loading") {
      return () => {
        cancelled = true;
      };
    }

    if (authGate === "signin") {
      replaceIfDifferent("/(owner)/onboarding/signin");
      return () => {
        cancelled = true;
      };
    }

    if (authGate === "error") {
      return () => {
        cancelled = true;
      };
    }

    if (!ownerId) {
      return () => {
        cancelled = true;
      };
    }

    if (isPhoneBackupRoute) {
      setReadyContextKey(accessContextKey);
      return () => {
        cancelled = true;
      };
    }

    void (async () => {
      try {
        const businessesSnap = await getDocs(
          query(
            collection(db, "businesses"),
            where("ownerId", "==", ownerId),
            limit(50)
          )
        );

        if (cancelled) return;

        const businesses = businessesSnap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...(docSnap.data() as Omit<GuardBusiness, "id">),
          }))
          .sort((a, b) => {
            const aMs = toMillis(a.updatedAt) || toMillis(a.createdAt);
            const bMs = toMillis(b.updatedAt) || toMillis(b.createdAt);
            return bMs - aMs;
          });
        const explicitlyRequested = requestedBusinessId
          ? businesses.find((business) => business.id === requestedBusinessId)
          : null;

        if (explicitlyRequested?.isDeleted === true) {
          replaceIfDifferent("/(owner)/dashboard");
          return;
        }

        const selected = selectOwnerLifecycleBusiness(
          businesses,
          requestedBusinessId
        );

        if (!selected) {
          replaceIfDifferent("/(owner)/start");
          return;
        }

        const status = normalizeOwnerOnboardingStatus(selected.status);
        const correctionAuthorized =
          status === "rejected" || status === "changes_requested"
            ? await hasOwnerCorrectionSession(ownerId, selected.id)
            : false;

        if (cancelled) return;

        // Only drafts resume through the index. The mounted index handles the
        // saved step after this gate has allowed the nested Stack to mount.
        if (isOnboardingIndex && status !== "draft") {
          replaceIfDifferent("/(owner)/dashboard");
          return;
        }

        const routeDecision = resolveOwnerOnboardingRouteDecision({
          businesses,
          requestedBusinessId,
          approvedEditMode,
          correctionAuthorized,
        });

        if (routeDecision.destination !== "allow") {
          replaceIfDifferent("/(owner)/dashboard");
          return;
        }

        setBusinessId(routeDecision.business.id ?? selected.id);
        setLifecycleAccess({ correctionAuthorized, approvedEditMode });
        setReadyContextKey(accessContextKey);
      } catch {
        if (cancelled) return;
        console.warn("Owner onboarding lifecycle guard failed.");
        replaceIfDifferent("/(owner)/dashboard");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    authGate,
    accessContextKey,
    isOnboardingIndex,
    isPhoneBackupRoute,
    isSignInRoute,
    ownerId,
    requestedBusinessId,
    approvedEditMode,
    setBusinessId,
    setLifecycleAccess,
  ]);

  // Sign-in must keep its navigator mounted while auth changes. Its own screen
  // waits for the user and owner readiness before following returnTo.
  if (isSignInRoute) return <>{children}</>;

  if (authGate === "error") {
    return (
      <View style={styles.centered}>
        <Text style={styles.title}>Account setup failed</Text>
        <Text style={styles.message}>
          You are signed in, but Soko Grid could not prepare your owner account.
          Please try again.
        </Text>
      </View>
    );
  }

  if (!guardReady) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator />
        <Text style={styles.loading}>Checking owner access...</Text>
      </View>
    );
  }

  return <>{children}</>;
}

const styles = {
  centered: {
    flex: 1,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    padding: 20,
    backgroundColor: "#ffffff",
  },
  title: {
    fontSize: 18,
    fontWeight: "900" as const,
    color: "#111827",
    textAlign: "center" as const,
  },
  message: {
    marginTop: 10,
    color: "#6b7280",
    lineHeight: 20,
    textAlign: "center" as const,
  },
  loading: {
    marginTop: 10,
    fontWeight: "800" as const,
    color: "#111827",
  },
};
